import { Alert, Stack, Typography } from '@mui/material';
import JITConfigField from 'components/JITDisabledField';
import LoadingButton from 'components/shared/LoadingButton';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import {
	useConfigs,
	useFeatureEnabledForConfig,
	useJITDisabled,
	useUpdateConfigs
} from 'api/configurations';
import useIsXaaEnabled from 'hooks/useIsXaaEnabled';
import { updateJITDisabled, updateXaaJITDisabled } from 'utils/saveConfigs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ROUTES } from 'constants/routes';
import { S4Feature } from 'types/api';
import { useNav } from 'utils/navigation';

const JitConfigPage: React.FC = () => {
	const { isSubTenant } = useIdPConfigContext();
	const nav = useNav();
	// Config-aware, not the plain useFeatureEnabled: JIT provisions the user this connection never
	// creates, and a bookmarked URL reaches this page without passing the sidebar that hides it.
	const { enabled: jitGuideEnabled } = useFeatureEnabledForConfig(
		S4Feature.JitGuide
	);
	const xaaEnabled = useIsXaaEnabled();
	useEffect(() => {
		if (!jitGuideEnabled) {
			nav(ROUTES.home);
		}
	}, [jitGuideEnabled, nav]);
	const { data: configsData, isLoading } = useConfigs();
	const { data: jitDisabled } = useJITDisabled();
	const updateConfigsMutation = useUpdateConfigs(
		intl.tr('scimConfig.saveJit.saveSuccessMessage'),
		intl.tr('scimConfig.saveJit.saveErrorMessage')
	);

	// SSO JIT toggle (unchanged behavior).
	const [localValue, setLocalValue] = useState<boolean | null>(null);
	const originalValue = jitDisabled ?? false;
	const currentValue = localValue !== null ? localValue : originalValue;

	// Cross-App Access JIT toggle - maps to the per-issuer jitDisabled. Only relevant
	// when the feature is on AND at least one trusted issuer exists.
	const xaaIssuers = useMemo(
		() => Object.values(configsData?.xaa?.jwtBearerSettings?.issuers ?? {}),
		[configsData]
	);
	const showXaaJit = xaaEnabled && xaaIssuers.length > 0;
	const [xaaLocalValue, setXaaLocalValue] = useState<boolean | null>(null);
	const xaaOriginalValue = xaaIssuers[0]?.jitDisabled ?? false;
	const xaaCurrentValue =
		xaaLocalValue !== null ? xaaLocalValue : xaaOriginalValue;

	const isDirty =
		(localValue !== null && localValue !== originalValue) ||
		(xaaLocalValue !== null && xaaLocalValue !== xaaOriginalValue);

	const handleFieldChange = useCallback((value: boolean) => {
		setLocalValue(value);
	}, []);

	const handleXaaFieldChange = useCallback((value: boolean) => {
		setXaaLocalValue(value);
	}, []);

	const handleSave = useCallback(async () => {
		/* istanbul ignore next */
		if (!configsData) {
			return;
		}

		try {
			let updatedConfig = configsData;
			if (localValue !== null && localValue !== originalValue) {
				updatedConfig = updateJITDisabled(localValue, updatedConfig);
			}
			if (xaaLocalValue !== null && xaaLocalValue !== xaaOriginalValue) {
				updatedConfig = updateXaaJITDisabled(xaaLocalValue, updatedConfig);
			}
			await updateConfigsMutation.mutateAsync(updatedConfig);
			setLocalValue(null);
			setXaaLocalValue(null);
		} catch (err) {
			// Error is handled by the mutation
		}
	}, [
		configsData,
		localValue,
		originalValue,
		xaaLocalValue,
		xaaOriginalValue,
		updateConfigsMutation
	]);

	if (!jitGuideEnabled) {
		return null;
	}

	return (
		<Stack gap={3}>
			{!isLoading && configsData && (
				<>
					<Typography variant="h2">
						{intl.tr('scimConfig.guide.jit.pageTitle')}
					</Typography>
					<Typography variant="body1">
						{intl.tr('scimConfig.guide.jit.pageSubheader')}
					</Typography>
					<JITConfigField
						value={currentValue}
						onChange={handleFieldChange}
						disabled={isLoading}
						name="jit-disabled-field"
					/>
					{showXaaJit && (
						<JITConfigField
							value={xaaCurrentValue}
							onChange={handleXaaFieldChange}
							disabled={isLoading}
							name="xaa-jit-disabled-field"
							label={intl.tr('jit.xaa.label')}
						/>
					)}
					<Stack gap={3}>
						{isSubTenant && (
							<Alert
								data-testid="sub-tenant-info-alert"
								variant="outlined"
								severity="info"
								sx={{ whiteSpace: 'pre', textWrap: 'auto' }}
							>
								{intl.tr('ssoConfig.guide.subtenant.info')}
							</Alert>
						)}
						<Stack alignItems="end">
							<LoadingButton
								disabled={!isDirty}
								data-testid="jit-save-button"
								variant="contained"
								onClick={handleSave}
							>
								{intl.tr('general.save')}
							</LoadingButton>
						</Stack>
					</Stack>
				</>
			)}
		</Stack>
	);
};

export default JitConfigPage;
